import {
	bidBondAmountTypeSchema,
	carrierName,
	closedReasonSchema,
	currencySchema,
	finalContractSuretyBondFormTypeSchema,
	isoDateSchema,
	isoDateTimeSchema,
	suretyBondId,
	suretyQuoteId,
	suretyQuoteStatusSchema,
	suretyQuoteUnderwritingId,
	suretyQuoteUnderwritingStatusSchema,
} from "@inrev/common";
import * as z from "zod";
import { bondFormSchema, obligeeSchema, projectSchema } from "../schemas/obligations";
import { reviewReasonSchema } from "../schemas/review-reasons";
import { defineTool } from "../tool-definition";

const bondRequestBase = {
	bondRequestId: suretyQuoteId,
	status: suretyQuoteStatusSchema,
	carrierName: carrierName.nullable(),
	bondAmount: currencySchema,
	premium: currencySchema.nullable(),
	rate: z.number().nullable(),
	tListingRequired: z.boolean().nullable(),
	reviewNote: z.string().nullable(),
	submittedAt: isoDateTimeSchema,
	reviewedAt: isoDateTimeSchema.nullable(),
	expiresAt: isoDateTimeSchema,
	project: projectSchema,
	obligees: z.array(obligeeSchema),
	form: bondFormSchema,
	closed: z
		.strictObject({
			reason: closedReasonSchema,
			closedAt: isoDateTimeSchema,
			note: z.string().nullable(),
		})
		.nullable(),
	underwriting: z
		.strictObject({
			underwritingId: suretyQuoteUnderwritingId,
			status: suretyQuoteUnderwritingStatusSchema,
			stale: z.boolean(),
			reviewedAt: isoDateTimeSchema.nullable(),
			reviewReasons: z.array(reviewReasonSchema),
		})
		.nullable(),
	bondId: suretyBondId.nullable(),
};

export const getBondRequest = defineTool({
	name: "get_bond_request",
	description:
		"One bond request on the account. In a bond-request conversation it is that request and `bondRequestId` is ignored; on the account pass `bondRequestId` from `list_bond_requests`. `contractSuretyType` is `bid`, `bid_to_final` or `final`, and the fields after it differ by type. A bid request carries `bidAmount`, the bid, and how the bond amount is set from it. A bid-to-final request carries `bidBondId` and `bidBondNumber`, the bid bond it replaces, `contractAmount`, and the bid results: `actualBidAmount` is the account's winning bid, `secondBid` the next-lowest. A final request carries `contractAmount`. `bondAmount` is the bond requested; on a bid request that is the bid bond's face amount, a share of the bid. `underwriting` is the request's latest underwriting run; its `reviewReasons` include inactive ones, and each `reason` names a `review_reason:bond_request:<reason>` knowledge slice. Account-level reasons are on `get_underwriting`, not here. `project` is the job: description, city and state, NAICS scope of work, months to complete, warranty, contract hazards and damages. `obligees` and `form` come from the bond form; obligees stay empty until the agency fills it. `bondId` is the bond a bound request became. What the request counts toward exposure is on `derive_open_exposure`.",
	label: "Bond request",
	contexts: ["account", "bond_request"],
	implementation: "api",
	audience: "model",
	input: z.strictObject({
		bondRequestId: suretyQuoteId.optional(),
	}),
	data: z.discriminatedUnion("contractSuretyType", [
		z.strictObject({
			...bondRequestBase,
			contractSuretyType: z.literal("bid"),
			bidAmount: currencySchema,
			bidDate: isoDateSchema.nullable(),
			bidBondAmountType: bidBondAmountTypeSchema,
			bondAmountPercentOfBid: z.number(),
			finalBondFormType: finalContractSuretyBondFormTypeSchema.nullable(),
		}),
		z.strictObject({
			...bondRequestBase,
			contractSuretyType: z.literal("bid_to_final"),
			bidBondId: suretyBondId,
			bidBondNumber: z.string().nullable(),
			contractAmount: currencySchema,
			contractDate: isoDateSchema.nullable(),
			actualBidAmount: currencySchema.nullable(),
			actualBidDate: isoDateSchema.nullable(),
			secondBid: currencySchema.nullable(),
		}),
		z.strictObject({
			...bondRequestBase,
			contractSuretyType: z.literal("final"),
			contractAmount: currencySchema,
		}),
	]),
});
